import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from 'src/i18n';
import '../account/d-dialog-context.js';
import '../../library/elements/d-action.js';
import { DScreenNetworkConfigDialog } from '../../content/screens/network-config/d-screen-network-config-dialog';

/**
 * Offentlig, uautentisert side for QR-deeplinken /screen-setup/<playerCode>.
 *
 * Viser kun nettverkskonfig-dialogen rett på koden – ingen meny, ingen
 * kontovelger, ingen kryss-org-skjerm-søk. BLE/captive-portal-provisjonering er
 * en lokal operasjon som krever fysisk nærhet til boksen, så vi konfigurerer
 * en vilkårlig boks uten innlogging. Siden hoster sin egen <d-dialog-context>
 * (global singleton som BaseDialog.open() skriver til).
 */
@customElement('d-screen-setup')
export class DScreenSetup extends LitElement {
  @property({ type: String })
  deviceCode = '';

  /** True mens dialogen er åpen, så vi ikke viser «åpne igjen»-knappen samtidig. */
  @state()
  private dialogOpen = false;

  static readonly styles = css`
    :host {
      display: block;
    }
    .page {
      display: flex;
      flex-direction: column;
      align-items: center;
      min-height: 100vh;
      /* Hardkodet Trinnvis-blå med vilje: dette er en isolert, offentlig side
         som ikke skal arve app-temaet. Ikke bytt til en CSS-variabel. */
      background: hsl(196, 83%, 49%);
      color: white;
      box-sizing: border-box;
      padding: 0 20px;
    }
    .logo {
      width: 137px;
      height: 45px;
      margin: 24px 0 48px;
      background: url(/images/trinnvis-logo-white.svg) left center no-repeat;
      background-size: 97px 25px;
      text-indent: -10000px;
      overflow: hidden;
      align-self: flex-start;
    }
    .card {
      width: 100%;
      max-width: 480px;
      text-align: center;
      margin-top: 8vh;
    }
    h1 {
      font-family: var(--serif), serif;
      font-weight: 200;
      font-size: 28px;
      margin: 0 0 12px;
    }
    p {
      font-size: 15px;
      line-height: 1.5;
      opacity: 0.9;
      margin: 0 0 24px;
    }
    d-action {
      --themeColor: white;
    }
    .back {
      margin-top: 32px;
    }
    .back a {
      color: white;
      opacity: 0.8;
      font-size: 14px;
      text-decoration: underline;
    }
  `;

  protected async firstUpdated() {
    // DDialogContext.instance settes synkront i d-dialog-context sin
    // connectedCallback (kjører før firstUpdated), så dialogen kan åpnes med en
    // gang. Uten en kode har vi ingen boks å konfigurere – da viser vi kun
    // feilteksten og lar være å åpne dialogen (unngår dobbel feilmelding).
    if (this.deviceCode) {
      await this.openDialog();
    }
  }

  private async openDialog() {
    this.dialogOpen = true;
    try {
      await DScreenNetworkConfigDialog.open({ screenName: '', deviceCode: this.deviceCode });
    } finally {
      this.dialogOpen = false;
    }
  }

  render() {
    const network = this.deviceCode ? `Trinnvis-${this.deviceCode.toUpperCase()}` : '';
    return html`
      <d-dialog-context></d-dialog-context>
      <div class="page">
        <div class="logo">Trinnvis</div>
        <div class="card">
          <h1>${t('screens.networkConfig.title')}</h1>
          <p>
            ${network ? t('screens.networkConfig.introText', { network }) : t('screens.networkConfig.errorMissingCode')}
          </p>
          ${this.dialogOpen
            ? nothing
            : html`
                <d-action plain button @click=${() => this.openDialog()}>
                  ${t('screens.networkConfig.connectToScreen')}
                </d-action>
              `}
          <!-- Escape hatch: en innlogget bruker som skanner QR-koden ved et uhell
               må komme seg videre til appen når dialogen lukkes. -->
          <div class="back"><a href="/">${t('appCore.backToStart')}</a></div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-screen-setup': DScreenSetup;
  }
}
